import {
  createContext,
  ReactNode,
  useState,
  useContext,
  useCallback,
  useEffect,
} from "react";
import { Professional } from "../@data";
import {
  createProfessional,
  getAllProfessionals,
  updateProfessional,
  deleteProfessional,
  enableOrDisableProfessional,
} from "../services/professionals";
import { useNotification } from "./notification";

export interface ProfessionalsContextModel {
  professionals: Professional[];
  loading: boolean;
  fetchProfessionals: () => void;
  createProfessional: (
    professional: Omit<Professional, "id" | "createdAt" | "updatedAt">
  ) => Promise<void>;
  updateProfessional: (
    id: string,
    data: Partial<Professional>
  ) => Promise<void>;
  deleteProfessional: (id: string) => Promise<void>;
  toggleProfessionalStatus: (id: string, enable: boolean) => Promise<void>;
}

const ProfessionalsContext = createContext<ProfessionalsContextModel>(
  {} as ProfessionalsContextModel
);

export const ProfessionalsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [loading, setLoading] = useState(false);
  const { handleNotification } = useNotification();

  const fetchProfessionals = useCallback(() => {
    setLoading(true);
    getAllProfessionals()
      .then((data) => {
        setProfessionals(data);
      })
      .catch((error: any) => {
        handleNotification(
          "error",
          error.message || "Erro ao buscar profissionais"
        );
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const createProfessionalHandler = useCallback(
    async (
      professional: Omit<Professional, "id" | "createdAt" | "updatedAt">
    ) => {
      setLoading(true);
      try {
        await createProfessional(professional);
        handleNotification("success", "Profissional criado com sucesso!");
        fetchProfessionals();
      } catch (error: any) {
        handleNotification(
          "error",
          error.message || "Erro ao criar profissional"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateProfessionalHandler = useCallback(
    async (id: string, data: Partial<Professional>) => {
      setLoading(true);
      try {
        await updateProfessional(id, data);
        handleNotification("success", "Profissional atualizado com sucesso!");
        fetchProfessionals();
      } catch (error: any) {
        handleNotification(
          "error",
          error.message || "Erro ao atualizar profissional"
        );
      } finally {
        setLoading(false);
      }
    },
    [fetchProfessionals, handleNotification]
  );

  const deleteProfessionalHandler = useCallback(async (id: string) => {
    setLoading(true);
    try {
      await deleteProfessional(id);
      handleNotification("success", "Profissional excluído com sucesso!");
      fetchProfessionals();
    } catch (error: any) {
      handleNotification(
        "error",
        error.message || "Erro ao excluir profissional"
      );
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleProfessionalStatus = useCallback(
    async (id: string, enable: boolean) => {
      setLoading(true);
      try {
        await enableOrDisableProfessional(id, enable);
        handleNotification(
          "success",
          `Profissional ${enable ? "habilitado" : "desabilitado"} com sucesso!`
        );
        fetchProfessionals();
      } catch (error: any) {
        handleNotification(
          "error",
          error.message || "Erro ao habilitar/desabilitar profissional"
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchProfessionals();
  }, [fetchProfessionals]);

  return (
    <ProfessionalsContext.Provider
      value={{
        professionals,
        loading,
        fetchProfessionals,
        createProfessional: createProfessionalHandler,
        updateProfessional: updateProfessionalHandler,
        deleteProfessional: deleteProfessionalHandler,
        toggleProfessionalStatus,
      }}
    >
      {children}
    </ProfessionalsContext.Provider>
  );
};

export const useProfessionals = () => {
  const context = useContext(ProfessionalsContext);
  if (!context) {
    throw new Error(
      "useProfessionals must be used within a ProfessionalsProvider"
    );
  }
  return context;
};
